import {
  Component,
  ElementRef,
  OnInit,
  Output,
  Input,
  EventEmitter,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef, OnChanges, OnDestroy, AfterViewInit
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { DisplayService } from "../services/display.service";
import OlMap from "ol/Map";
import { PDFService } from "../services/pdf.service";
import { LayerService } from "../services/layer.service";
import { UserService } from "../services/user.service";
import { MatMenuTrigger } from "@angular/material/menu";
import * as loadingLogo from "../add-layer/LMI-loading-logo";
import { Subscription } from "rxjs";
import { SearchQueryService } from "../services/search-query.service";
import { MatExpansionPanel } from "@angular/material/expansion";
import { EditFeatureService } from "../map/query-views/services/edit-feature.service";
import { searchQueryRequest, mapLayer, UserProfileAction, layerTree, AppText, Languages, ApplicationGroup, LayerType } from "../interfaces/common";

@Component({
  selector: "app-main-nav",
  templateUrl: "./main-nav.component.html",
  styleUrls: ["./main-nav.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [MatExpansionPanel]
})
export class MainNavComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Output() selectedLang = new EventEmitter();
  @Output() toggleTitle = new EventEmitter();
  @Output() openUserLayerSettingsEE = new EventEmitter();
  @Output() updateSwitchApplication = new EventEmitter();

  @Input() selText: AppText = {};
  @Input() applicationName: string = "";
  @Input() settings: any;
  @Input() applicationSettings: any;
  @Input() mapObject: OlMap = new OlMap();
  @Input() lang: Languages = Languages.Icelandic;
  @Input() isMobileView: boolean = false;
  @Input() mapLayers: mapLayer[] = [];
  @Input() apps: ApplicationGroup[] = [];

  searchString: FormControl<string | null> = new FormControl<string | null>("");  // Contains the current string in the searchBox
  titleIcon: HTMLLinkElement | null = null;
  isShowingLocation: boolean = false;
  layerTree: layerTree | null = null
  basemapLayerTree: layerTree | null = null
  searchBoxCounter: number = -1;  // Keeps track of if the searchBox is currently open
  measureToolsAvailable: boolean = false;
  disablePDFScreenshot: boolean = false;
  userLoginAvailable: boolean = false;
  userFullName: string = "";
  userIsCurrentlyLoggedIn: boolean = false;
  switchApplicationAvailable: boolean = false;
  baseMapsMenuOpen: boolean = false;
  mapsMenuOpen: boolean = false;
  isUserLoggedInSub: Subscription = new Subscription();
  aboutOpen: boolean = false;
  editUserProfileOpen: boolean = false;
  editAction: string = "";
  enableUserProfileEdit: boolean = false;
  userProfileAction = UserProfileAction;
  isDownloadingPDF = false;
  threePointHasContent: boolean = false;

  @ViewChild("searchMenuTrigger") searchMenuTrigger?: MatMenuTrigger;
  @ViewChild("loadingLogoContainer") loadingLogoContainer?: ElementRef;
  @ViewChild("mapsMenuTrigger") mapsMenuTrigger?: MatMenuTrigger;
  @ViewChild("basemapsMenuTrigger") basemapsMenuTrigger?: MatMenuTrigger;
  @ViewChild("threepointMapsMenuTrigger") threepointMapsMenuTrigger?: MatMenuTrigger;
  @ViewChild("threepointBasemapsMenuTrigger") threepointBasemapsMenuTrigger?: MatMenuTrigger;
  @ViewChild("threepointsMenuTrigger") threepointsMenuTrigger?: MatMenuTrigger;

  constructor(
    public displayService: DisplayService,
    public layerService: LayerService,
    private userService: UserService,
    public pdfService: PDFService,
    private editFeatureService: EditFeatureService,
    private searchQueryService: SearchQueryService,
    private changeDetector: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.changeTitle();
    this.titleIcon = document.querySelector("#applicationFavicon");

    if (this.selText.Favicon && this.titleIcon) this.titleIcon.href = this.selText.Favicon;

    // Determines if the user can change things like password, profile image, username and so on
    if (this.applicationSettings.option?.map?.map_tools?.enableUserProfileEdit) this.enableUserProfileEdit = true;

    this.measureToolsAvailable = this.displayService.measureTools_used;
    this.disablePDFScreenshot = this.displayService.disablePDFScreenshot;
    this.userLoginAvailable = this.displayService.getUserLoginAvailable();

    this.isUserLoggedInSub = this.userService
      .getIsUserLoggedIn()
      .subscribe((isLoggedIn: boolean | null) => {
        if (isLoggedIn) {
          this.userFullName = this.userService.getFullName() || this.userService.getUsername();
        }
        this.userIsCurrentlyLoggedIn = isLoggedIn ?? false;
        this.threePointHasContent = this.updateThreePointContent();
        this.changeDetector.markForCheck();
      });

    /*
      Set if swithcApplication is available to click.
      Its on by default if "applicationsGroups" is defined for application.
      It can be disabled if disableButton is true.
    */
    if (this.applicationSettings?.option?.applicationsGroups && !this.applicationSettings?.option?.applicationsGroups?.disableButton) {
      this.switchApplicationAvailable = true;
    }
    this.threePointHasContent = this.updateThreePointContent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.measureToolsAvailable = this.displayService.measureTools_used;
    this.disablePDFScreenshot = this.displayService.disablePDFScreenshot;
    /*
      lang is for getting possible different language legends when it changes 
      mapLayers is when layer has multiple styles and they might change when basemap is changed
    */
    if ((changes.hasOwnProperty("lang") && !changes?.lang.firstChange)) this.layerService.updateAllLegendURLs();

    if (changes.hasOwnProperty("isMobileView") && !changes['isMobileView'].firstChange) this.threePointHasContent = this.updateThreePointContent();
  }

  ngOnDestroy(): void {
    this.isUserLoggedInSub.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.checkOpenMenusAtInit()
    if (this.applicationSettings?.option?.startAbout) this.openAbout();
    this.disablePDFScreenshot = this.displayService.disablePDFScreenshot; // Refresh values as sibling components may have updated them during ngOnInit
    this.threePointHasContent = this.updateThreePointContent();
  }

  /*
    Checks if Gögn and/or Grunnkort should open automatically at init time. If so open them programatically.
    This method must be called in ngAfterViewInit() but not ngOnInit() because the reference
    to the menus are only ready when the view has renderer once.
  */
  checkOpenMenusAtInit(): void {
    if (this.applicationSettings?.option?.interface?.isSimple || this.isMobileView) return  // Don't open any menus if simple mode interface

    //Determines if mapsMenu and/or basemapsMenu should open automatically
    const openMapsMenu: boolean = this.applicationSettings?.option?.openMapsMenu;
    const openBasemapsMenu: boolean = this.applicationSettings?.option?.openBasemapsMenu;

    //Open menus automatically if its set to true in application settings in db
    if (openMapsMenu) {
      /*
        Use the threePoint trigger to open if we are in mobile mode.
        If we use mapsMenuTrigger when width < 900px then the menu will open in the wrong place (all the way left&top).
        This happens probably because the trigger button doesn't actually exist (display: none) but is instead in threePoint menu.
      */
      if (this.isMobileView) {
        // We must open the threePoint menu first before opening Gögn or Grunnkort, else it starts top-left corner
        this.threepointsMenuTrigger?.openMenu();
        this.threepointMapsMenuTrigger?.openMenu();
      }
      else this.mapsMenuTrigger?.openMenu();
    }

    if (openBasemapsMenu) {
      if (this.isMobileView) {
        this.threepointsMenuTrigger?.openMenu();
        this.threepointBasemapsMenuTrigger?.openMenu();
      }
      else this.basemapsMenuTrigger?.openMenu();
    }
    this.changeDetector.detectChanges();
  }

  /* 
    These next 4 methods are just here to destroy the menus when they are closed. Why destroy on close?
    Because we want the expanded state of the groups to keep it state when user closes and reopens the menu. Why doesnt it remember it automatically?
    Because of a bug in the material component MatExpansionPanel: https://github.com/angular/components/issues/20517
  */
  basemapsMenuClosed(): void {
    //timeout is here for aesthetic reasons, want the mat-menu closing animation to finsh before destroying it, else it looks ugly.  
    setTimeout(() => {
      this.baseMapsMenuOpen = false;
      this.changeDetector.detectChanges()
      /*
      Need to call to be absolutely sure that thumbnail will hide in case user clicks on a new basemap and quickly triggers mouseenter on another map,
      therefore showImage() might be called but hideImage() never.
      */
    }, 100);
  }

  basemapsMenuOpened(): void {
    this.baseMapsMenuOpen = true;
    if (this.mapsMenuOpen) {
      this.mapsMenuClosed();
      this.mapsMenuTrigger?.closeMenu();
    }
  }

  mapsMenuClosed(): void { 
    // Timeout is here for aesthetic reasons, want the mat-menu closing animation to finsh before destroying it, else it looks ugly.
    setTimeout(() => {
      this.mapsMenuOpen = false;
      this.changeDetector.detectChanges()
    }, 100);
    
  }

  mapsMenuOpened(): void {
    this.mapsMenuOpen = true;
    if (this.baseMapsMenuOpen) {
      this.basemapsMenuClosed();
      this.basemapsMenuTrigger?.closeMenu();
    }
  }

  toggleCurrentLocation(): void {
    if (this.isShowingLocation) {
      this.displayService.removeCurrentLocation();
      this.isShowingLocation = false;
    }
    else {
      this.displayService.getCurrentLocation();
      this.isShowingLocation = true;
    }
  }

  toggleInterface(): void {
    this.displayService.toggleInterface();
  }

  // Set all layers where username is not null into "Mín gagnalög" | "My Layers" Group layer
  setUserLayersIntoGroup(sourceMapLayers: mapLayer[], mapLayers: mapLayer[]): void {
    let userLayerGroup: mapLayer | undefined = this.layerService.findLayerByName("userLayers");
    if (userLayerGroup === undefined) {
      console.warn("Could not find a userLayer Group layer for this application in config, creating one for you...");

      const userLayerObj: mapLayer = {
        attributions: "Notendalög",
        basemap: false,
        checked: false,
        disabled: false,
        id: 830,
        imagetype: "image/png",
        layer: null,
        layermenu: 1,
        layerorder: 700000,
        matrix: "",
        metadata: "",
        name: "userLayers",
        onlyquery: false,
        option: null,
        query: false,
        query_option: null,
        style: { string: "" },
        title: "Mín gagnalög",
        title_lang: { is: "Mín gagnalög", en: "My Layers" },
        type: LayerType.GROUP,
        url: "",
        username: null,
        layermenu2: [1],
        explodedGroup: false,
        hide: false
      };
      sourceMapLayers.push(userLayerObj);
      userLayerGroup = userLayerObj;
    }

    const userLayerGroupID: number = userLayerGroup.id;
    for (let i = 0; i < mapLayers.length; i++) {
      if (mapLayers[i].username) {
        mapLayers[i].layermenu = userLayerGroupID;
      }
    }
  }

  initiateLogin(): void {
    this.userService.setAppToLoginScreen();
  }

  logOut(): void {
    this.displayService.setMultipleActiveStates(false, false, false, false, false);
    this.userService.logout();
  }

  listUserLayers(): void {
    this.userService.listUserLayers();
  }

  getUserStyles(): void {
    this.userService.getUserStyles();
  }

  /**
   * Toggle language
   */
  changeLang(): void {
    this.lang = this.lang == Languages.Icelandic ? Languages.English : Languages.Icelandic;
    this.selectedLang.emit(this.lang);
    document.getElementById("mapid")?.focus({ preventScroll: true });
    this.displayService.generateLink();
  }

  changeTitle(): void {
    let title = "";

    if (this.selText.title) title += this.selText.title;

    if (this.selText.name) {
      if (title) title += " - ";
      title += this.selText.name;
    }

    if (title) this.toggleTitle.emit(title);
  }

  searchChange(): void {
    const searchEventObj: searchQueryRequest = {
      searchValue: this.searchString.value ?? "",
      cameFrom: "Change",
    };
    this.searchQueryService.setNewSearchQuery(searchEventObj);
  }

  searchPaste(event: ClipboardEvent): void {
    const pastedSearchQuery = event.clipboardData?.getData("text/plain") ?? "";
    const searchEventObj: searchQueryRequest = {
      searchValue: pastedSearchQuery,
      cameFrom: "Paste",
    };
    this.searchQueryService.setNewSearchQuery(searchEventObj);
  }

  searchButton(): void {
    const searchValue = this.searchString.value || ""; // Use an empty string as a default value if it's null
    const searchEventObj: searchQueryRequest = {
      searchValue: searchValue,
      cameFrom: "Button",
    };
    this.searchQueryService.setNewSearchQuery(searchEventObj);
    document.getElementById("mapid")?.focus({ preventScroll: true });
  }

  openUserLayerSettings(layer: mapLayer): void {
    const layerName: string = layer.name;
    this.openUserLayerSettingsEE.emit(layerName);
    this.displayService.toggleAddLayerActive();
  }

  activateMeasureTool(): void {
    this.displayService.flipMeasureToolsVisable();
    this.editFeatureService.setSelectInteractionActive(false);
  }

  downloadPDF(): void {
    if(this.isDownloadingPDF) return; // Prevent multiple clicks on the PDF button starting multiple downloads
    this.isDownloadingPDF = true;

    // Start the loading animation
    const logo: HTMLCanvasElement = loadingLogo.getLoadingLogo(600, 600);
    logo.classList.add("loadingLogo");
    loadingLogo.startAnimation();
    this.loadingLogoContainer?.nativeElement.appendChild(logo);

    this.pdfService.getWholeMapPDF().subscribe({
      next: () => { },
      error: () => {
        this.cleanupPDFDownload(logo);
      },
      complete: () => {
        this.cleanupPDFDownload(logo);
        this.changeDetector.markForCheck();
      }
    });
  }

  private cleanupPDFDownload(logo: HTMLCanvasElement): void {
    loadingLogo.pauseAnimation();
    logo.remove();
    this.isDownloadingPDF = false;
  }

  updateMap(map: OlMap): void {
    this.mapObject = map;
  }

  toggleSwitchApplication(): void {
    this.updateSwitchApplication.emit(true);
    // Close all menus when user enters switchApplication feature. (Maybe temp, but its difficult to make menus appear underneath the switchApplication feature and also have tooltip working)
    this.mapsMenuTrigger?.closeMenu();
    this.basemapsMenuTrigger?.closeMenu();
    this.threepointBasemapsMenuTrigger?.closeMenu();
    this.threepointsMenuTrigger?.closeMenu();
    this.searchMenuTrigger?.closeMenu();
  }

  keepFocus(): void {
    this.displayService.setFocus();
  }

  toggleUploadFilesDialog(): void {
    this.displayService.toggleUploadFilesActive();
  }

  toggleAddLayersDialog(): void {
    this.displayService.toggleAddLayerActive();
  }
 
  openAbout(): void {
    this.aboutOpen = true;
  }

  closeAbout(): void {
    this.aboutOpen = false;
  }

  toggleAbout(): void {
    if (this.aboutOpen) this.closeAbout();
    else this.openAbout();
  }

  toggle3D(): void {
    this.displayService.set3DModeActive(!this.displayService.is3DModeActive$.value)
  }

  openEditUserProfile(action: string): void {
    this.editAction = action;
    this.editUserProfileOpen = true;
  }

  closeEditUserProfile(): void {
    this.editUserProfileOpen = false;
    this.editAction = "";
  }
  /**
   * Toggles light and dark mode theme via changing class to "dark" or "light" on the body element.
   */
  toggleDarkLightMode(): void {
    this.displayService.setColorTheme(this.displayService.colorTheme === "light" ? "dark" : "light", true)
  }

  private updateThreePointContent(): boolean {
    if (this.isMobileView) return true;
    if (this.displayService.mapviewInterface?.canToggle && !this.applicationSettings?.option?.iconsInMainNavBar?.interfaceMode) return true;
    if (this.selText.url && !this.applicationSettings?.option?.iconsInMainNavBar?.url) return true;
    if (!this.disablePDFScreenshot && !this.applicationSettings?.option?.iconsInMainNavBar?.screenshot) return true;
    if (!this.userIsCurrentlyLoggedIn && this.userLoginAvailable === true && !this.applicationSettings?.option?.iconsInMainNavBar?.login) return true;
    if (this.displayService.enableSplitMap && !this.applicationSettings?.option?.iconsInMainNavBar?.splitMap) return true;
    if (!this.applicationSettings?.option?.disableUserCanToggleTheme && !this.applicationSettings?.option?.iconsInMainNavBar?.theme) return true;
    if (this.displayService.hideAbout === false && !this.applicationSettings?.option?.iconsInMainNavBar?.about) return true;
    if (!this.applicationSettings?.option?.iconsInMainNavBar?.language) return true;
    return false;
  }
}